import _ from 'lodash';
import moment from 'moment-timezone';
import * as api from '../../../api';

import * as store from '../../../reducers/store';
import { InsuranceCoverage } from 'vivaa-api/dist/models/previsit/patient/insurance';
import { getSourceFieldValue, isValueContained } from '../index';
import { ConditionType } from 'vivaa-api/dist/models/previsit/formTemplateFieldConfig';
import { isInScoreRange } from './score';
import { getValueFromFieldOrPatient } from '../../../components/utils';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

export const getUseAlternateEnum = (
  pageFormFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  displayableFormFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
): boolean => {
  return (
    (pageFormFields.length === 1 ||
      (displayableFormFields.length === 1 &&
        !canShowMorePrimitive(displayableFormFields[0], pageFormFields))) &&
    displayableFormFields[0].primitive === Primitive.Enum &&
    !!displayableFormFields[0].options &&
    displayableFormFields[0].options.length < 6
  );
};

export const checkConditions = ({
  conditions,
  page,
  rank,
  fields,
  patient,
}: {
  conditions: api.PrevisitFormTemplateFieldConfig.Condition[];
  page: number;
  rank: number;
  fields: store.FormFieldWithConfig[];
  patient: api.Patient.Patient | null;
}): boolean => {
  for (const condition of conditions) {
    const {
      sourcePrevisitFormTemplateFieldConfigId,
      acceptValues,
      rejectValues,
      requireMultiAcceptValues,
      property,
    } = condition;
    const sourceField = fields.find(
      (f) =>
        sourcePrevisitFormTemplateFieldConfigId &&
        [f.previsitFormTemplateFieldConfigId, f.id].includes(
          sourcePrevisitFormTemplateFieldConfigId,
        ),
    );

    if (condition.type === ConditionType.QUESTION) {
      if (!sourceField) continue;
      const values = getTargetValuesForCondition(sourceField, property);
      if (acceptValues) {
        const matchingValues = _.intersection(acceptValues, values);
        if (requireMultiAcceptValues && matchingValues.length < 2) {
          return false;
        }
        if (matchingValues.length < 1) return false;
      }

      if (rejectValues && isValueContained(rejectValues, values)) {
        return false;
      }
    } else if (condition.type === ConditionType.AGE) {
      if (!isInAgeMinMax({ patient, fields, ...condition })) return false;
    } else if (condition.type === ConditionType.SCORE) {
      if (
        !isInScoreRange({
          fields,
          page,
          rank,
          min: condition.min,
          max: condition.max,
        })
      )
        return false;
    }
  }
  return true;
};

const isInAgeMinMax = (args: {
  fields: store.FormFieldWithConfig[];
  patient: api.Patient.Patient | null;
  ageMin?: api.PrevisitFormTemplateFieldConfig.Condition['ageMin'];
  ageMax?: api.PrevisitFormTemplateFieldConfig.Condition['ageMax'];
}): boolean => {
  const { ageMin, ageMax, patient, fields } = args;
  const patientDob = getValueFromFieldOrPatient(
    api.PrevisitFormTemplateFieldConfig.Primitive.PatientDob,
    fields,
    patient,
  );
  if (!patientDob) return false;
  const age = moment().diff(patientDob, 'years');
  if ((ageMin && ageMin > age) || (ageMax && age > ageMax)) {
    return false;
  }
  return true;
};

const getTargetValuesForCondition = (
  sourceField: store.FormFieldWithConfig,
  conditionProperty?: string | null,
) => {
  let rawValues = getSourceFieldValue(sourceField.value, sourceField.primitive);
  switch (sourceField.primitive) {
    case Primitive.PatientInsurance:
    case Primitive.PatientInsuranceCardUpload:
      return getTargetValuesForInsurance(rawValues, conditionProperty);
    case Primitive.PatientAllergies:
      return rawValues.map((value) => (!value ? 'NO_PATIENT_ALLERGIES' : ''));
    case Primitive.PatientCareProvidersPicker:
      return rawValues.map((value) =>
        !value
          ? api.PrevisitPatientCareTeam.PcpSelectionStatus.NoPcpSelected
          : value === api.PrevisitPatientCareTeam.PcpSelectionStatus.NoPcpFound
            ? api.PrevisitPatientCareTeam.PcpSelectionStatus.NoPcpFound
            : '',
      );
    default:
      return conditionProperty
        ? _.flatten(rawValues.map((rawValue) => rawValue[conditionProperty]))
        : rawValues;
  }
};

const getTargetValuesForInsurance = (
  rawValues: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue[],
  conditionProperty?: string | null,
) => {
  switch (conditionProperty) {
    case api.PrevisitFormTemplateFieldConfig.InsuranceCardUploadProperties
      .Payors:
      return _.flatten(
        rawValues.map((rawValue) =>
          rawValue.insuranceCoverages?.map(
            (coverage: InsuranceCoverage) => coverage.payor,
          ),
        ),
      );
    case api.PrevisitFormTemplateFieldConfig.InsuranceCardUploadProperties
      .PayWithInsurance:
      return rawValues.map((value) =>
        value.payingWithInsurance
          ? api.PrevisitFormTemplateFieldConfig.PayWithInsuranceConditionOptions
              .PayWithInsurance
          : api.PrevisitFormTemplateFieldConfig.PayWithInsuranceConditionOptions
              .PayOutOfPocket,
      );
    case api.PrevisitFormTemplateFieldConfig.InsuranceCardUploadProperties
      .Modified:
      return rawValues.map((value) => {
        const notPayingWithInsurance = value.payingWithInsurance === false;
        const hasAddedInsurances = !!value.addedInsuranceIds?.length;
        const hasRemovedInsurances = !!value.invalidInsuranceIds?.length;
        return notPayingWithInsurance ||
          hasAddedInsurances ||
          hasRemovedInsurances
          ? api.PrevisitFormTemplateFieldConfig.BooleanConditionOptions.True
          : api.PrevisitFormTemplateFieldConfig.BooleanConditionOptions.False;
      });
    default:
      return [];
  }
};

const canShowMorePrimitive = (
  visibleFormField: api.PrevisitFormField.PrevisitFormFieldWithConfig,
  displayableFormFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
): boolean => {
  return displayableFormFields
    .filter((field) => field.id !== visibleFormField.id)
    .some((field) => {
      if (!field.conditions.length) return false;
      return field.conditions.some(
        (condition) =>
          condition.sourcePrevisitFormTemplateFieldConfigId ===
          visibleFormField.previsitFormTemplateFieldConfigId,
      );
    });
};
